import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Divider, GoogleButton } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { useAuth } from "../auth.context";
import { authErrorMessage } from "../auth.errors";
import { DEFAULT_DESTINATION } from "../auth.routes";
import { useGoogleIdentity } from "../useGoogleIdentity";

export interface GoogleSectionProps {
  /**
   * Texto del botón de RESPALDO (el deshabilitado que se enseña mientras
   * GIS carga o cuando no está disponible). El botón real lo pinta Google
   * y su texto se elige con `intent`.
   */
  label?: string;
  /** "signup" pone el texto de registro en el botón oficial de Google. */
  intent?: "signin" | "signup";
  /**
   * Pinta el divisor "O" sobre el botón. En el login separa dos caminos
   * equivalentes y ayuda. En el wizard el botón ya viene detrás de
   * "Siguiente", así que la alternativa se entiende sola y el divisor solo
   * gasta el alto que necesita el paso para caber en pantalla.
   */
  withDivider?: boolean;
  /** Se deshabilita mientras el formulario de correo está enviándose. */
  disabled?: boolean;
  /** A dónde ir al entrar. El login pasa la ruta de la que se echó a alguien. */
  destination?: string;
}

/**
 * Divisor + botón de Google + el aviso si algo falló.
 *
 * Vive aquí y no en cada pantalla porque login y registro lo pintan
 * idéntico: si mañana Google cambia de sitio o de texto, cambia una vez.
 *
 * El botón lo pinta GIS (el oficial de Google: es quien entrega el
 * `credential`). Mientras el script carga —o si nunca carga: red caída,
 * bloqueador, falta el client id— se enseña nuestro `GoogleButton`
 * deshabilitado con un `title` que explica por qué: nunca un botón muerto
 * que se pueda pulsar sin que pase nada.
 */
export function GoogleSection({
  label,
  intent = "signin",
  disabled = false,
  withDivider = true,
  destination,
}: GoogleSectionProps) {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCredential = useCallback(
    (credential: string) => {
      setError(null);
      setSubmitting(true);
      loginWithGoogle(credential)
        .then(() => {
          // `replace` para que el botón de atrás no regrese al login ya
          // resuelto — igual que el login con contraseña.
          navigate(destination ?? DEFAULT_DESTINATION, { replace: true });
        })
        .catch((cause: unknown) => {
          setSubmitting(false);
          setError(authErrorMessage(cause));
        });
    },
    [loginWithGoogle, navigate, destination],
  );

  const google = useGoogleIdentity({
    onCredential,
    text: intent === "signup" ? "signup_with" : "continue_with",
  });

  return (
    <div className={withDivider ? "mt-6 grid gap-4" : "mt-4 grid gap-3"}>
      {withDivider && <Divider label="O" />}

      {error && (
        <Alert variant="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/*
        El div de GIS queda SIEMPRE montado (aunque invisible mientras
        carga): si se montara al llegar a `ready`, el ref no existiría en
        el momento en que el efecto quiere pintar el botón dentro.
      */}
      <div
        ref={google.buttonRef}
        aria-busy={submitting || undefined}
        className={cn(
          "flex justify-center",
          google.status !== "ready" && "hidden",
          // Mientras el formulario de al lado envía —o este POST está en
          // vuelo— el botón de Google no debe aceptar otro clic.
          (disabled || submitting) && "pointer-events-none opacity-60",
        )}
      />

      {google.status !== "ready" && (
        <GoogleButton
          label={label}
          // Sin spinner: nadie lo ha pulsado. Solo deshabilitado, con el
          // `title` contando en qué está.
          disabled
          title={
            google.status === "loading"
              ? "Conectando con Google…"
              : "Entrar con Google no está disponible ahora mismo. Usa tu correo y contraseña."
          }
        />
      )}
    </div>
  );
}
